import React, { FC, memo, useCallback } from 'react';
import { IconLock } from '@tabler/icons-react';
import { DndProvider, useDrag, useDrop, XYCoord } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import classNames from 'classnames';
import * as styles from '../../styles.module.scss';
import { DataTableColumn } from '../../utils/types';
import { saveColumnOrder } from '../../utils/handlers';
import { useTranslation } from '../../../../hooks/useTranslation';

interface ManageColumnModalProps {
  columns: DataTableColumn[];
  onToggleColumn: (key: string) => void;
  onReorderColumns: (columns: DataTableColumn[]) => void;
  tableId: string;
  onResetColumns?: () => void;
}

interface DraggableColumnProps {
  column: DataTableColumn;
  index: number;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  onToggleColumn: (key: string) => void;
}

const DraggableColumn: FC<DraggableColumnProps> = memo(
  ({ column, index, moveColumn, onToggleColumn }) => {
    const ref = React.useRef<HTMLDivElement>(null);

    const [{ isDragging }, drag] = useDrag({
      type: 'COLUMN',
      item: { type: 'COLUMN', index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: !column.locked && column.fixed === undefined,
    });

    const [, drop] = useDrop({
      accept: 'COLUMN',
      hover(item: { index: number }, monitor) {
        if (!ref.current) return;

        const dragIndex = item.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) return;

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        moveColumn(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const isInteractive = !column.locked && column.fixed === undefined;
    if (isInteractive) {
      drag(drop(ref));
    }

    return (
      <div
        ref={ref}
        className={classNames(
          'col-12 mb-1',
          styles.draggableColumn,
          isDragging && styles.dragging,
        )}
        style={{ cursor: isInteractive ? 'move' : 'default' }}
      >
        <label className="form-selectgroup-item flex-fill mb-1">
          <input
            type="checkbox"
            className="form-selectgroup-input"
            checked={!column.isHide}
            onChange={() => onToggleColumn(column.key)}
            disabled={column.locked || column.fixed !== undefined}
          />
          <div
            className={classNames(
              'form-selectgroup-label d-flex align-items-center p-3',
              styles.label,
            )}
          >
            <div className="me-3">
              <span className="form-selectgroup-check" />
            </div>
            <div className="d-flex justify-content-between w-100">
              <span>{`${index + 1}. ${column.label}`}</span>
              {(column.locked || column.fixed) && (
                <IconLock size={18} className="ms-2" />
              )}
            </div>
          </div>
        </label>
      </div>
    );
  },
);

const ManageColumnModal: FC<ManageColumnModalProps> = memo(
  ({ columns, onToggleColumn, onReorderColumns, tableId, onResetColumns }) => {
    const { t } = useTranslation();

    const moveColumn = useCallback(
      (dragIndex: number, hoverIndex: number) => {
        const dragColumn = columns[dragIndex];
        const newColumns = [...columns];
        newColumns.splice(dragIndex, 1);
        newColumns.splice(hoverIndex, 0, dragColumn);
        onReorderColumns(newColumns);
        saveColumnOrder(newColumns, tableId);
      },
      [columns, onReorderColumns, tableId],
    );

    return (
      <div
        className="modal modal-blur fade"
        id="manageColumnModal"
        tabIndex={-1}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t('MANAGE_COLUMNS')}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body row">
              <DndProvider backend={HTML5Backend}>
                {columns.map((column, index) => (
                  <DraggableColumn
                    key={column.key}
                    column={column}
                    index={index}
                    moveColumn={moveColumn}
                    onToggleColumn={onToggleColumn}
                  />
                ))}
              </DndProvider>
            </div>
            <div className="modal-footer">
              {onResetColumns && (
                <a
                  href="#"
                  className="me-auto text-decoration-underline"
                  onClick={onResetColumns}
                >
                  {t('RESET_TO_DEFAULT')}
                </a>
              )}
              <button
                type="button"
                className="btn btn-primary"
                data-bs-dismiss="modal"
              >
                {t('SAVE')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

DraggableColumn.displayName = 'DraggableColumn';
ManageColumnModal.displayName = 'ManageColumnModal';

export default ManageColumnModal;
